export interface Theme {
  id: string;
  name: string;
  description: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    foreground: string;
    muted: string;
    border: string;
    success: string;
    warning: string;
    error: string;
  };
  fonts: {
    heading: string;
    body: string;
    mono: string;
  };
  effects: {
    animation: 'smooth' | 'glitch' | 'retro' | 'minimal';
    scanlines: boolean;
    noise: boolean;
    glow: boolean;
    cursor: 'default' | 'crosshair' | 'pointer' | 'terminal';
  };
  sounds: {
    enabled: boolean;
    click: string;
    success: string;
    error: string;
  };
}

export const themes: Record<string, Theme> = {
  default: {
    id: 'default',
    name: 'Modern Professional',
    description: 'Clean, modern design for professional environments',
    colors: {
      primary: '#2563eb',
      secondary: '#64748b',
      accent: '#06b6d4',
      background: '#ffffff',
      foreground: '#0f172a',
      muted: '#f1f5f9',
      border: '#e2e8f0',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
    },
    fonts: {
      heading: 'Inter, system-ui, sans-serif',
      body: 'Inter, system-ui, sans-serif',
      mono: 'JetBrains Mono, Courier New, monospace',
    },
    effects: {
      animation: 'smooth',
      scanlines: false,
      noise: false,
      glow: false,
      cursor: 'default',
    },
    sounds: {
      enabled: false,
      click: '',
      success: '',
      error: '',
    },
  },

  // ============================================
  // VAULT-TEC RETRO THEME (Classic Terminal)
  // But with better readability than before
  // ============================================
  falloutClassic: {
    id: 'falloutClassic',
    name: 'Vault-Tec Retro',
    description: 'Authentic Fallout terminal - classic amber phosphor CRT display',
    colors: {
      // Authentic Fallout terminal colors (Amber phosphor)
      primary: '#FF9F1C',      // Amber/orange terminal text
      secondary: '#FFBF69',    // Light amber
      accent: '#FFC200',       // Vault-Boy Gold
      background: '#0d0d0d',   // Near-black CRT background
      foreground: '#FF9F1C',   // Amber phosphor glow
      muted: '#8B6914',        // Dimmed amber
      border: '#3A2C1A',       // Dark amber border
      success: '#22c55e',      // Green-500
      warning: '#FF9F1C',      // Amber
      error: '#ef4444',        // Red-500
    },
    fonts: {
      heading: 'VT323, "Courier New", monospace',
      body: 'VT323, "Courier New", monospace',
      mono: 'VT323, "Courier New", monospace',
    },
    effects: {
      animation: 'glitch',
      scanlines: true,         // Light scanlines for authenticity
      noise: false,            // No noise for readability
      glow: true,
      cursor: 'crosshair',
    },
    sounds: {
      enabled: true,
      click: '/sounds/terminal-click.mp3',
      success: '/sounds/terminal-success.mp3',
      error: '/sounds/terminal-error.mp3',
    },
  },

  // ============================================
  // MINECRAFT THEME (Professional)
  // Stone-gray aesthetic based on official Minecraft logo
  // ============================================
  minecraft: {
    id: 'minecraft',
    name: 'Minecraft',
    description: 'Minecraft theme with grass green accents',
    colors: {
      // Minecraft colors with green emphasis
      primary: '#5A9E4D',      // Grass green - primary color
      secondary: '#2E7D32',    // Dark green
      accent: '#8BC34A',       // Light green accent
      background: '#F5F5F5',   // Very light gray (clean)
      foreground: '#252326',   // Dark stone gray (highly readable)
      muted: '#9E9E9E',        // Medium gray (disabled)
      border: '#BDBDBD',       // Light gray border
      success: '#5A9E4D',      // Grass green
      warning: '#FF9800',      // Orange (lava)
      error: '#D32F2F',        // Red (damage)
    },
    fonts: {
      heading: '"Press Start 2P", "VT323", monospace',
      body: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      mono: '"Press Start 2P", "VT323", monospace',
    },
    effects: {
      animation: 'smooth',
      scanlines: false,
      noise: false,
      glow: false,
      cursor: 'default',
    },
    sounds: {
      enabled: false,
      click: '/sounds/minecraft-click.mp3',
      success: '/sounds/minecraft-levelup.mp3',
      error: '/sounds/minecraft-damage.mp3',
    },
  },
};

export const getTheme = (themeId: string): Theme => {
  return (themes as any)[themeId] || themes.default;
};

export const getAvailableThemes = (): Theme[] => {
  return Object.values(themes);
};
